"use client"

import { useMemo, useState } from "react";
import { Clipboard } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

const normalizeUrl = (value: string): string => {
    if (!value) return "";
    const trimmed = value.trim();

    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        return trimmed;
    }

    return `https://${trimmed}`;
};

const extractParts = (rawValue: string) => {
    const normalized = normalizeUrl(rawValue);

    try {
        const parsedUrl = new URL(normalized);
        const hostnameParts = parsedUrl.hostname.split(".");
        const tld = hostnameParts.length > 1 ? hostnameParts[hostnameParts.length - 1] : "";
        const domainCore = hostnameParts.length > 1 ? hostnameParts.slice(-2).join(".") : parsedUrl.hostname;
        const subdomain = hostnameParts.length > 2 ? hostnameParts.slice(0, -2).join(".") : "";
        const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
        const fileName = pathSegments.length ? pathSegments[pathSegments.length - 1] : "";
        const extension = fileName.includes(".") ? fileName.split(".").pop() : "";
        const query = parsedUrl.search ? parsedUrl.search.substring(1) : "";
        const hash = parsedUrl.hash ? parsedUrl.hash.substring(1) : "";

        const searchParams = Array.from(parsedUrl.searchParams.entries()).map(([key, value]) => ({ key, value }));

        return {
            protocol: parsedUrl.protocol.replace(":", ""),
            hostname: parsedUrl.hostname,
            domain: domainCore,
            tld,
            subdomain,
            port: parsedUrl.port || (parsedUrl.protocol.replace(":", "") === "http" ? "80" : "443"),
            path: parsedUrl.pathname === "/" ? "(root)" : parsedUrl.pathname,
            fileName,
            extension,
            hash,
            query,
            searchParams,
            length: String(parsedUrl.href.length),
        };
    } catch {
        return null;
    }
};

export function UrlDeconstructor() {
    const [input, setInput] = useState("https://subdomain.example.com:8080/path/to/page.html?param1=value1&param2=value2#section");
    const { messages } = useTranslations();

    const parts = useMemo(() => extractParts(input), [input]);

    return (
        <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{messages.tools.urlDeconstructor.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{messages.tools.urlDeconstructor.title}</h1>
                    <p className="text-sm text-gray-400">{messages.tools.urlDeconstructor.description}</p>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <div className="relative flex-1">
                        <Input
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            placeholder={messages.tools.urlDeconstructor.placeholder}
                            aria-label={messages.tools.urlDeconstructor.placeholder}
                            className="flex-1 h-12 border border-slate-800 bg-transparent text-slate-100 placeholder:text-gray-500 pr-14 ring-2 ring-cyan-500/25 focus:ring-cyan-500/60"
                        />
                        <button
                            type="button"
                            onClick={() => copyToClipboard(input)}
                            className="group absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                            aria-label={messages.tools.urlDeconstructor.copyInputLabel}
                        >
                            <Clipboard className="h-5 w-5 text-current transition-colors group-hover:text-cyan-400" />
                        </button>
                    </div>
                    <Button
                        type="button"
                        onClick={() => setInput("https://subdomain.example.com:8080/path/to/page.html?param1=value1&param2=value2#section")}
                        className="h-10 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                    >
                        {messages.tools.urlDeconstructor.useSample}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {parts ? (
                    <>
                        <InfoCard label={messages.tools.urlDeconstructor.protocol} value={parts.protocol} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.hostname} value={parts.hostname} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.domain} value={parts.domain} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.tld} value={parts.tld || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.subdomain} value={parts.subdomain || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.port} value={parts.port} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.path} value={parts.path} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.file} value={parts.fileName || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.extension} value={parts.extension || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.hash} value={parts.hash || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.queryString} value={parts.query || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <InfoCard label={messages.tools.urlDeconstructor.urlLength} value={parts.length || "-"} copyLabelTemplate={messages.tools.urlDeconstructor.copyLabelTemplate} />
                        <div className="md:col-span-2 rounded-xl border border-cyan-800/50 bg-slate-950/60 p-4">
                            <div className="mb-2 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-gray-200">{messages.tools.urlDeconstructor.queryParams}</h3>
                                {parts.searchParams.length ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            copyToClipboard(
                                                parts.searchParams
                                                    .map(({ key, value }) => `${key}=${value}`)
                                                    .join("\n") || ""
                                            )
                                        }
                                        className="group inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                                        aria-label={messages.tools.urlDeconstructor.copyQueryParamsLabel}
                                    >
                                        <Clipboard className="h-5 w-5 text-current transition-colors group-hover:text-cyan-400" />
                                    </button>
                                ) : null}
                            </div>
                            {parts.searchParams.length ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {parts.searchParams.map((param) => (
                                        <div key={`${param.key}-${param.value}`} className="rounded-lg bg-slate-900/60 border border-slate-800/80 p-3">
                                            <p className="text-xs uppercase tracking-wide text-gray-400">{param.key}</p>
                                            <div className="mt-1 flex items-center justify-between gap-2">
                                                <p className="text-sm font-medium text-white break-all">{param.value || messages.tools.urlDeconstructor.emptyValue}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => copyToClipboard(param.value || messages.tools.urlDeconstructor.emptyValue)}
                                                    className="group inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                                                    aria-label={`${messages.tools.urlDeconstructor.copyValueLabel} ${param.key}`}
                                                >
                                                    <Clipboard className="h-5 w-5 text-current transition-colors group-hover:text-cyan-400" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">{messages.tools.urlDeconstructor.noQueryParams}</p>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="col-span-1 md:col-span-2 rounded-xl border border-rose-800/50 bg-rose-950/60 p-4 text-rose-100">
                        {messages.tools.urlDeconstructor.invalidUrl}
                    </div>
                )}
            </div>
        </div>
    );
}

function InfoCard({ label, value, copyLabelTemplate }: { label: string; value: string; copyLabelTemplate: string }) {
    return (
        <div className="rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="mt-1 text-base font-medium text-white break-all">{value}</p>
                </div>
                <button
                    type="button"
                    onClick={() => copyToClipboard(value)}
                    className="group inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                    aria-label={copyLabelTemplate.replace("{label}", label)}
                >
                    <Clipboard className="h-5 w-5 text-current transition-colors group-hover:text-cyan-400" />
                </button>
            </div>
        </div>
    );
}
